import React from 'react';

import { useAuthentication } from '@context/AuthContext';
import { useCurrentUser } from '@context/UsersContext';
import { useGroupsQuery } from '@db/Groups/Queries';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { Group } from '@types';

export type GroupsContextProps = {
  userGroups: Group[];
  userGroupIds: Set<string>;
};

export const GroupsContext = createNamedContext<GroupsContextProps>({
  GroupsContext: undefined,
});

export function useGroups() {
  return useRequiredContext(GroupsContext);
}

export function GroupsProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();
  const { userDoc } = useCurrentUser();
  const userGroupIds = React.useMemo(
    () => new Set([...(userDoc?.groupIds || []), ...(userDoc?.adminGroupIds || [])]),
    [userDoc?.adminGroupIds, userDoc?.groupIds]
  );
  const userGroupIdList = React.useMemo(() => [...userGroupIds.values()], [userGroupIds]);
  const userGroupsQuery = useGroupsQuery({
    enabled: loggedIn,
    groupIds: userGroupIdList,
  });

  const value = React.useMemo(
    () => ({ userGroups: userGroupsQuery.groups, userGroupIds }),
    [userGroupsQuery.groups, userGroupIds]
  );

  return <GroupsContext.Provider value={value}>{children}</GroupsContext.Provider>;
}
